import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import { Plus, FileText, Zap, Settings, ChevronLeft, ChevronRight, ChevronDown, LogOut, BarChart3 } from 'lucide-react'
import { Permission } from 'extra/types/Permissions'
import { useAuth } from '../context/AuthContext'
import { useEntities } from '../context/EntitiesContext'
import { RequirePermission } from './RequirePermission'

const SIDEBAR_STORAGE_KEY = 'pantheon_sidebar_state'

interface SidebarState {
  collapsed: boolean
  collapsedFolders: Record<string, boolean>
  collapsedUncategorized: boolean
}

function loadSidebarState (): SidebarState {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Failed to load sidebar state:', error)
  }
  return { collapsed: false, collapsedFolders: {}, collapsedUncategorized: false }
}

function saveSidebarState (state: SidebarState): void {
  try {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error('Failed to save sidebar state:', error)
  }
}

function PromptLink ({ prompt, collapsed, active }: { prompt: { id: string; name: string }; collapsed: boolean; active: boolean }) {
  return (
    <Link
      to={`/prompts/${prompt.id}`}
      className={`
        flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors text-sm
        ${active ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
        ${collapsed ? 'justify-center' : ''}
      `}
      title={prompt.name}
    >
      <FileText className="h-4 w-4 flex-shrink-0"/>
      {!collapsed && <span className="truncate">{prompt.name}</span>}
    </Link>
  )
}

export function Sidebar () {
  const { prompts, folders, loading } = useEntities()
  const location = useLocation()
  const { logout } = useAuth()
  const [collapsed, setCollapsed] = useState(() => loadSidebarState().collapsed)
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>(() => loadSidebarState().collapsedFolders)
  const [collapsedUncategorized, setCollapsedUncategorized] = useState(() => loadSidebarState().collapsedUncategorized)

  // Save state to localStorage whenever it changes
  useEffect(() => {
    saveSidebarState({ collapsed, collapsedFolders, collapsedUncategorized })
  }, [collapsed, collapsedFolders, collapsedUncategorized])

  const toggleSidebar = () => setCollapsed(!collapsed)

  const toggleFolder = (folderId: string) => {
    setCollapsedFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }))
  }

  const toggleUncategorized = () => {
    setCollapsedUncategorized(!collapsedUncategorized)
  }

  const isActive = (path: string) => location.pathname === path
  const isActivePrefix = (path: string) => location.pathname.startsWith(path)

  // Group prompts by folder
  const uncategorizedPrompts = prompts
    .filter(p => p.folderId === null)
    .sort((a, b) => a.name.localeCompare(b.name))

  const folderGroups = folders
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(folder => ({
      folder,
      prompts: prompts
        .filter(p => p.folderId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter(group => group.prompts.length > 0)

  return (
    <aside className={`${collapsed ? 'w-16' : 'w-48'} bg-gray-900 border-r border-gray-700 h-full flex flex-col transition-all duration-300`}>
      {/* Title */}
      <div className={`p-3 border-b border-gray-700 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
        {!collapsed && <h1 className="text-xl font-bold text-white truncate">pantheon</h1>}
        <button
          onClick={toggleSidebar}
          className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 flex flex-col">
        {/* New Prompt */}
        <Link
          to="/prompts/new"
          className={`
            flex items-center gap-3 px-2 py-1.5 rounded-lg mb-2 transition-colors text-sm
            ${isActive('/prompts/new')
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }
          ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? 'New Prompt' : undefined}
        >
          <Plus className="h-5 w-5 flex-shrink-0"/>
          {!collapsed && <span>New Prompt</span>}
        </Link>

        {/* Prompts Section */}
        {!collapsed && (
          <div className="mb-2 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Prompts
          </div>
        )}
        {collapsed && <div className="h-2"></div>}

        {loading ? (
          !collapsed && <div className="px-3 py-2 text-gray-500 text-xs">Loading...</div>
        ) : prompts.length === 0 ? (
          !collapsed && <div className="px-3 py-2 text-gray-500 text-xs">No prompts yet</div>
        ) : (
          <div className="space-y-3">
            {/* Uncategorized Prompts */}
            {uncategorizedPrompts.length > 0 && (
              <div>
                {!collapsed && (
                  <button
                    onClick={toggleUncategorized}
                    className="w-full px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-400 uppercase tracking-wide flex items-center gap-1 transition-colors"
                  >
                    {collapsedUncategorized
                      ? (
                      <ChevronRight className="h-3 w-3 flex-shrink-0" />
                        )
                      : (
                      <ChevronDown className="h-3 w-3 flex-shrink-0" />
                        )}
                    <span>Uncategorized</span>
                  </button>
                )}
                {!collapsedUncategorized && (
                  <div className="space-y-1 mt-1">
                    {uncategorizedPrompts.map((prompt) => (
                      <PromptLink key={prompt.id} prompt={prompt} collapsed={collapsed} active={isActivePrefix(`/prompts/${prompt.id}`)} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Folder Groups */}
            {folderGroups.map(({ folder, prompts: folderPrompts }) => (
              <div key={folder.id}>
                {!collapsed && (
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    className="w-full px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-400 uppercase tracking-wide flex items-center gap-1 transition-colors"
                  >
                    {collapsedFolders[folder.id]
                      ? (
                      <ChevronRight className="h-3 w-3 flex-shrink-0" />
                        )
                      : (
                      <ChevronDown className="h-3 w-3 flex-shrink-0" />
                        )}
                    <span>{folder.name}</span>
                  </button>
                )}
                {!collapsedFolders[folder.id] && (
                  <div className="space-y-1 mt-1">
                    {folderPrompts.map((prompt) => (
                      <PromptLink key={prompt.id} prompt={prompt} collapsed={collapsed} active={isActivePrefix(`/prompts/${prompt.id}`)} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Divider */}
        <div className="my-2 border-t border-gray-700"/>

        {/* Executions Link */}
        <RequirePermission permission={Permission.VIEW_EXECUTIONS}>
          <Link
            to="/executions"
            className={`
              flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors text-sm
              ${isActive('/executions')
              ? 'bg-blue-600 text-white'
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
            }
            ${collapsed ? 'justify-center' : ''}
            `}
            title={collapsed ? 'Executions' : undefined}
          >
            <Zap className="h-5 w-5 flex-shrink-0"/>
            {!collapsed && <span>Executions</span>}
          </Link>
        </RequirePermission>

        {/* Stats Link */}
        <Link
          to="/executions/stats"
          className={`
            flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors text-sm
            ${isActive('/executions/stats')
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }
          ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? 'Stats' : undefined}
        >
          <BarChart3 className="h-5 w-5 flex-shrink-0"/>
          {!collapsed && <span>Stats</span>}
        </Link>

        {/* Settings Link */}
        <Link
          to="/settings"
          className={`
            flex items-center gap-3 px-2 py-1.5 rounded-lg transition-colors text-sm
            ${isActive('/settings')
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
          }
          ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? 'Settings' : undefined}
        >
          <Settings className="h-5 w-5 flex-shrink-0"/>
          {!collapsed && <span>Settings</span>}
        </Link>

        <div className="my-2 border-t border-gray-700"/>
        <div className="grow"/>
        {collapsed
          ? (
            <button
                onClick={logout}
                className="flex items-center justify-center p-2 rounded-lg text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
                title="Logout"
            >
                <LogOut className="h-5 w-5" />
            </button>
            )
          : (
            <button
            onClick={logout}
            className="btn btn-danger-light text-xs px-3 py-1.5 w-full flex items-center justify-center gap-2"
            >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
            </button>
            )}
      </nav>
    </aside>
  )
}
